import { useState } from 'react';
import { Avatar } from './Avatar';

/**
 * A Company/Vendor identity mark — the bundled logo when the row has one, the monogram
 * {@link Avatar} when it does not.
 *
 * **The fallback is the point.** `logo` (040) is nullable and most rows will never carry
 * one, so this component exists so that no call site has to branch: every directory row and
 * every profile header renders `<Logo …>` and gets a correctly-sized tile either way. That
 * is also what makes adopting the column a non-event — a row without a mark looks exactly as
 * it did before.
 *
 * **Slug → path, resolved here and nowhere else.** The DB stores `furuno-electric`, not a
 * URL, precisely so the location of the artwork is the web's business (see the migration's
 * header for why a URL and inline bytes were both rejected). This is the one place that
 * mapping lives, so moving the assets is a one-line change.
 *
 * **A broken asset degrades to the monogram rather than to a broken-image glyph.** A slug
 * can outlive its file — a row is edited to name a mark that was never drawn, or an asset is
 * renamed — and the browser's default for that is a torn-page icon with the alt text beside
 * it, which looks like a bug on every row at once. `onError` flips to the same fallback the
 * null case uses, so the failure mode is "no logo yet", which is true and is not alarming.
 *
 * **Sizing and a11y come from `Avatar`'s tile.** The `<img>` wears the same `.avatar` class,
 * so one CSS rule owns the 30px box and the 9px radius for both branches and they cannot
 * drift apart. `alt=""` + `aria-hidden` for the same reason `Avatar` is hidden by default:
 * on every current call site the name is rendered as text immediately beside the mark, and
 * announcing both makes the row read its subject twice. Pass `label` for a standalone use.
 */
export function Logo({
  id,
  name,
  logo,
  label,
  size,
}: {
  /** Stable subject id — drives the monogram hue on the fallback path. */
  id: string;
  /** Display name: the monogram source, and the accessible name when `label` is set. */
  name: string;
  /** Bundled asset slug from the API. Absent → monogram. */
  logo?: string;
  /** Accessible name. Omit when adjacent text already names the subject. */
  label?: string;
  /** Tile size — default is the 30px row tile, `'lg'` the 44px page-header one. */
  size?: 'lg';
}): React.JSX.Element {
  const [failed, setFailed] = useState(false);

  if (logo === undefined || failed) {
    return (
      <Avatar
        id={id}
        name={name}
        {...(label === undefined ? {} : { label })}
        {...(size === undefined ? {} : { size })}
      />
    );
  }

  return (
    <img
      className="avatar"
      {...(size === undefined ? {} : { 'data-size': size })}
      src={`/logos/${logo}.svg`}
      onError={() => setFailed(true)}
      {...(label === undefined
        ? { alt: '', 'aria-hidden': true }
        : { alt: label, role: 'img' })}
    />
  );
}
